/**
 * Amounts, exactly as the server computes them — and only ever as a preview.
 *
 * The server recalculates every figure on save and its answer is the one that
 * is stored. This file exists so the user sees a total change as they type,
 * not so the client decides anything. Where the two could disagree, the form
 * shows the server's numbers after saving.
 *
 * The rounding rules are copied deliberately from ProformaCalculator, because
 * a preview that disagrees with the saved document by one rupiah reads as a
 * bug in the system even when the saved document is right:
 *
 *  - line amounts round per line;
 *  - VAT sums every line carrying the same tax code first, applies the rate to
 *    that sum, and rounds once;
 *  - the rounded VAT is then split back across those lines on the running
 *    total, so the parts add back to it exactly.
 *
 * Withholding still rounds per line, as on the server.
 */

/** HALF_UP, matching BigDecimal on the server. */
export function roundHalfUp(value: number, scale: number): number {
  if (!Number.isFinite(value)) return 0
  const factor = 10 ** scale
  const scaled = value * factor
  // Binary representation turns 36666.63 into ...6299999. Nudging by a few
  // ulps before rounding keeps the boundary cases on the same side as
  // BigDecimal puts them.
  const nudge = Math.abs(scaled) * Number.EPSILON * 8
  return (Math.sign(scaled) * Math.round(Math.abs(scaled) + nudge)) / factor
}

export interface PreviewLine {
  quantity: number
  unitPrice: number
  discountPct?: number | null
  discountAmt?: number | null
  taxCode?: string | null
  taxRate: number
  whtRate: number
}

/** VAT for one tax code: the base it was charged on, and the single figure. */
export interface PreviewTaxBucket {
  taxCode: string
  rate: number
  baseAmt: number
  taxAmt: number
}

export interface PreviewTotals {
  lines: Array<{ lineAmt: number; taxAmt: number; whtAmt: number; discountAmt: number }>
  taxBuckets: PreviewTaxBucket[]
  subtotal: number
  discountTotal: number
  taxTotal: number
  whtTotal: number
  total: number
  amountDue: number
}

export function previewTotals(
  scale: number,
  wapu: boolean,
  lines: PreviewLine[],
): PreviewTotals {
  const computed = lines.map((line) => {
    const gross = roundHalfUp((line.quantity || 0) * (line.unitPrice || 0), scale)
    const discountAmt =
      line.discountAmt != null && line.discountAmt !== 0
        ? roundHalfUp(line.discountAmt, scale)
        : roundHalfUp((gross * (line.discountPct || 0)) / 100, scale)
    const lineAmt = gross - discountAmt
    // Multiply before dividing: it keeps whole-rupiah inputs exact in binary
    // floating point for every realistic document size.
    const whtAmt = roundHalfUp((lineAmt * (line.whtRate || 0)) / 100, scale)
    return { lineAmt, taxAmt: 0, whtAmt, discountAmt }
  })

  // Group by code and rate, not by rate alone: two codes can share a rate today
  // and diverge when one changes, and the faktur pajak reports per code. A line
  // at 0% is charged nothing and joins no bucket — "not subject to VAT" and
  // "VAT of nil" print alike but only the second belongs on a tax invoice.
  const groups = new Map<string, number[]>()
  lines.forEach((line, index) => {
    if (!line.taxRate) return
    const key = `${line.taxCode ?? ''}@${line.taxRate}`
    const group = groups.get(key)
    if (group) group.push(index)
    else groups.set(key, [index])
  })

  const taxBuckets: PreviewTaxBucket[] = []
  for (const group of groups.values()) {
    const first = lines[group[0]!]!
    const baseAmt = group.reduce((acc, index) => acc + computed[index]!.lineAmt, 0)
    const taxAmt = roundHalfUp((baseAmt * first.taxRate) / 100, scale)

    // Allocate on the running total, so the per-line shares add back to taxAmt
    // exactly instead of drifting a rupiah away from it.
    if (baseAmt !== 0) {
      let runningBase = 0
      let allocated = 0
      for (const index of group) {
        runningBase += computed[index]!.lineAmt
        const shareSoFar = roundHalfUp((taxAmt * runningBase) / baseAmt, scale)
        computed[index]!.taxAmt = shareSoFar - allocated
        allocated = shareSoFar
      }
    }

    taxBuckets.push({ taxCode: first.taxCode ?? '', rate: first.taxRate, baseAmt, taxAmt })
  }

  const sum = (pick: (l: (typeof computed)[number]) => number) =>
    computed.reduce((acc, l) => acc + pick(l), 0)

  const subtotal = sum((l) => l.lineAmt)
  const taxTotal = taxBuckets.reduce((acc, bucket) => acc + bucket.taxAmt, 0)
  const whtTotal = sum((l) => l.whtAmt)
  const total = subtotal + taxTotal - whtTotal

  return {
    lines: computed,
    taxBuckets,
    subtotal,
    discountTotal: sum((l) => l.discountAmt),
    taxTotal,
    whtTotal,
    total,
    // Under WAPU the customer remits the VAT directly, so it leaves the
    // document's value intact and reduces only what is collected.
    amountDue: wapu ? total - taxTotal : total,
  }
}

export function formatAmount(value: number, scale: number): string {
  return new Intl.NumberFormat('id-ID', {
    minimumFractionDigits: scale,
    maximumFractionDigits: scale,
  }).format(Number.isFinite(value) ? value : 0)
}

/**
 * "IDR 96.200.000", "USD 12.480,00": rupiah without decimals, other
 * currencies with two. A dash when the amount is not known yet.
 */
export function formatMoney(currency: string | null | undefined, amount: number | null | undefined): string {
  if (amount == null) return '—'
  const code = currency || 'IDR'
  return `${code} ${formatAmount(amount, code === 'IDR' ? 0 : 2)}`
}

const ONES = [
  '',
  'satu',
  'dua',
  'tiga',
  'empat',
  'lima',
  'enam',
  'tujuh',
  'delapan',
  'sembilan',
  'sepuluh',
  'sebelas',
]

function spell(n: number): string {
  if (n < 12) return ONES[n] ?? ''
  if (n < 20) return `${spell(n - 10)} belas`
  if (n < 100) return `${spell(Math.floor(n / 10))} puluh ${spell(n % 10)}`
  if (n < 200) return `seratus ${spell(n - 100)}`
  if (n < 1_000) return `${spell(Math.floor(n / 100))} ratus ${spell(n % 100)}`
  if (n < 2_000) return `seribu ${spell(n - 1_000)}`
  if (n < 1_000_000) return `${spell(Math.floor(n / 1_000))} ribu ${spell(n % 1_000)}`
  if (n < 1_000_000_000) return `${spell(Math.floor(n / 1_000_000))} juta ${spell(n % 1_000_000)}`
  if (n < 1_000_000_000_000)
    return `${spell(Math.floor(n / 1_000_000_000))} miliar ${spell(n % 1_000_000_000)}`
  return `${spell(Math.floor(n / 1_000_000_000_000))} triliun ${spell(n % 1_000_000_000_000)}`
}

/**
 * The amount in words.
 *
 * Not decoration: Indonesian financial documents carry it, and its absence is
 * a certain UAT finding. Kept here rather than on the server because it is
 * presentation — but it must be identical on the printed document, so when the
 * PDF is built this is the implementation to port, not to rewrite.
 */
export function terbilang(value: number, currency = 'IDR'): string {
  const whole = Math.floor(Math.abs(value))
  const words = whole === 0 ? 'nol' : spell(whole).replace(/\s+/g, ' ').trim()
  const unit = currency === 'IDR' ? 'rupiah' : currency
  const fraction = Math.round((Math.abs(value) - whole) * 100)

  const sign = value < 0 ? 'minus ' : ''
  const base = `${sign}${words} ${unit}`
  const full = fraction > 0 ? `${base} ${spell(fraction).trim()} sen` : base
  return full.charAt(0).toUpperCase() + full.slice(1)
}
